import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useMemo,
  useState,
} from 'react'
import {
  KibSuperSelect,
  type KibSuperSelectOption,
} from '@chewy/kib-fields-new-react'
import type { Field } from '@pbt/pbt-ui-components'

import {
  generateQuantitySuggestions,
  getOneUomPluralized,
} from '../../inventoryUtils'

import styles from './QuantityAutocompleteField.module.scss'

interface QuantityAutocompleteFieldProps {
  autoFocus?: boolean
  disabled?: boolean
  field: Field
  invalid?: boolean
  label?: string
  messagesContent?: ReactNode
  orderedQuantity: number
  packageTypeName: string
  perPackageAmount: number
  productSizeUnit: string
  required?: boolean
}

export const QuantityAutocompleteField = ({
  field,
  orderedQuantity,
  perPackageAmount,
  productSizeUnit,
  packageTypeName,
  disabled = false,
  required = false,
  label,
  invalid = false,
  messagesContent,
  autoFocus = false,
}: QuantityAutocompleteFieldProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const allOptions: KibSuperSelectOption[] = useMemo(() => {
    const suggestions = generateQuantitySuggestions(
      orderedQuantity,
      perPackageAmount,
      productSizeUnit,
      packageTypeName,
    )

    const options = suggestions.slice(0, 3).map((suggestion) => ({
      label: suggestion.label,
      value: suggestion.value.toString(),
    }))

    const fieldValueStr = field.value?.toString()
    if (fieldValueStr && !options.some((opt) => opt.value === fieldValueStr)) {
      const matchingSuggestion = suggestions.find(
        (s) => s.value.toString() === fieldValueStr,
      )
      return [
        {
          label:
            matchingSuggestion?.label ??
            `${field.value} ${getOneUomPluralized(productSizeUnit, field.value)}`,
          value: fieldValueStr,
        },
        ...options,
      ]
    }

    return options
  }, [
    orderedQuantity,
    perPackageAmount,
    productSizeUnit,
    packageTypeName,
    field.value,
  ])

  const selectedOption = useMemo(
    () =>
      field.value
        ? [{ label: field.value.toString(), value: field.value.toString() }]
        : [],
    [field.value],
  )

  const handleChange = (newOptions: KibSuperSelectOption[]) => {
    const numericValue = Number(newOptions[0]?.value)
    field.setValue(Number.isNaN(numericValue) ? null : numericValue)
  }

  const onQuery = (
    v?: string,
    e?:
      | ChangeEvent<HTMLInputElement>
      | ReactMouseEvent<HTMLElement>
      | FocusEvent<HTMLInputElement>
      | KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e?.type === 'change' && v !== undefined) {
      const cleanValue = v.replace(/\D/g, '')
      const numValue = Number(cleanValue)
      field.setValue(
        cleanValue === '' || Number.isNaN(numValue) ? null : numValue,
      )
    }
  }

  return (
    <div className={styles.quantityFieldScope}>
      <KibSuperSelect
        autoFocus={autoFocus}
        disabled={disabled}
        invalid={invalid}
        label={label || ''}
        messagesContent={messagesContent}
        options={isOpen ? allOptions : []}
        placeholder=""
        required={required}
        value={selectedOption}
        onBlur={() => setIsOpen(false)}
        onChange={handleChange}
        onFocus={() => setIsOpen(true)}
        onQuery={onQuery}
      />
    </div>
  )
}
